import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

type TermsSection = {
  heading: string;
  body: string;
};

type TermsPayload = {
  title: string;
  lastUpdated: string;
  sections: TermsSection[];
};

/** Supports camelCase or PascalCase JSON from the API. */
function mapTermsResponse(data: unknown): TermsPayload | null {
  if (data === null || typeof data !== "object") {
    return null;
  }
  const d = data as Record<string, unknown>;
  const title = (typeof d.title === "string" ? d.title : typeof d.Title === "string" ? d.Title : "") ?? "";
  const lastUpdated =
    (typeof d.lastUpdated === "string" ? d.lastUpdated : typeof d.LastUpdated === "string" ? d.LastUpdated : "") ?? "";
  const raw = d.sections ?? d.Sections;
  if (!Array.isArray(raw)) {
    return { title, lastUpdated, sections: [] };
  }
  const sections: TermsSection[] = raw.map((item) => {
    if (item === null || typeof item !== "object") {
      return { heading: "", body: "" };
    }
    const s = item as Record<string, unknown>;
    const heading = (typeof s.heading === "string" ? s.heading : typeof s.Heading === "string" ? s.Heading : "") ?? "";
    const body = (typeof s.body === "string" ? s.body : typeof s.Body === "string" ? s.Body : "") ?? "";
    return { heading, body };
  });
  return { title, lastUpdated, sections };
}

function trimTrailingPunctuationFromUrl(url: string): string {
  return url.replace(/[.,;:!?)]+$/u, "");
}

function parseSafeHttpsHref(raw: string): string | null {
  const candidate = trimTrailingPunctuationFromUrl(raw);
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "https:") {
      return null;
    }
    return candidate;
  } catch {
    return null;
  }
}

function renderBodyWithHttpsLinks(body: string): ReactNode {
  const re = /(https:\/\/[^\s<>"']+)/gu;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let linkKey = 0;
  re.lastIndex = 0;
  while ((match = re.exec(body)) !== null) {
    const rawMatch = match[1] ?? "";
    const start = match.index;
    if (start > lastIndex) {
      parts.push(body.slice(lastIndex, start));
    }
    const href = parseSafeHttpsHref(rawMatch);
    if (href === null) {
      parts.push(rawMatch);
    } else {
      parts.push(
        <Link
          key={`terms-body-link-${linkKey++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          variant="body1"
          color="inherit"
          underline="always"
          sx={{ wordBreak: "break-all" }}
        >
          {href}
        </Link>,
      );
    }
    lastIndex = start + rawMatch.length;
  }
  if (lastIndex < body.length) {
    parts.push(body.slice(lastIndex));
  }
  return parts.length === 0 ? body : parts;
}

const TermsAndConditionsPage = observer(() => {
  const appStore = useAppStore();
  const [terms, setTerms] = useState<TermsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const backHref = appStore.isAuthenticated ? "/projects" : "/";
  const backLabel = appStore.isAuthenticated ? i18n.t("terms-page.back-to-app") : i18n.t("terms-page.back-to-sign-in");

  useEffect(() => {
    let cancelled = false;
    let loadSeq = 0;

    const loadTerms = async () => {
      const requestSeq = ++loadSeq;
      try {
        const rawLng = i18n.resolvedLanguage || i18n.language || "en";
        const primary = rawLng.split("-")[0] || "en";
        const candidates = primary === "en" ? ["en"] : [primary, "en"];

        let mapped: TermsPayload | null = null;
        for (const code of candidates) {
          if (cancelled || requestSeq !== loadSeq) {
            return;
          }
          const res = await fetch(`/locales/${code}/terms-and-conditions.json`, {
            credentials: "same-origin",
            cache: "no-store",
          });
          if (cancelled || requestSeq !== loadSeq) {
            return;
          }
          if (!res.ok) {
            continue;
          }
          const data: unknown = await res.json();
          mapped = mapTermsResponse(data);
          if (mapped !== null && (mapped.title !== "" || mapped.sections.length > 0)) {
            break;
          }
        }

        if (cancelled || requestSeq !== loadSeq) {
          return;
        }
        if (mapped === null || (mapped.title === "" && mapped.sections.length === 0)) {
          setError(i18n.t("terms-page.load-error"));
          setTerms(null);
        } else {
          setTerms(mapped);
          setError(null);
        }
      } catch {
        if (!cancelled && requestSeq === loadSeq) {
          setError(i18n.t("terms-page.load-error"));
          setTerms(null);
        }
      }
    };

    void loadTerms();
    const onLanguageChanged = () => {
      void loadTerms();
    };
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      cancelled = true;
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Button component={RouterLink} to={backHref} startIcon={<ArrowBackIcon />} variant="text" sx={{ alignSelf: "flex-start" }}>
          {backLabel}
        </Button>

        {error !== null && (
          <Typography color="error" role="alert">
            {error}
          </Typography>
        )}

        {terms !== null && (
          <>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {terms.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("terms-page.last-updated", { date: terms.lastUpdated })}
              </Typography>
            </Box>

            {terms.sections.map((section, index) => (
              <Box key={`terms-section-${index}`}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {section.heading}
                </Typography>
                <Typography variant="body1" color="text.primary" component="div" sx={{ whiteSpace: "pre-line" }}>
                  {renderBodyWithHttpsLinks(section.body)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Stack>
    </Container>
  );
});

export default TermsAndConditionsPage;